import { CombinedStateCreator } from "../store";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase/supabaseClient";
import { clearUserCaches } from "@/lib/utils/clearUserCaches";
import { selectActiveSubscription } from "@/lib/billing/subscriptionCache";
import type {
  SessionEntitlements,
  SessionSubscription,
} from "@/lib/auth/sessionTypes";
import {
  DEFAULT_WEEK_STARTS_ON,
  readSessionWeekStartsOn,
  type WeekStartsOn,
} from "@/lib/dates/weekStart";

export interface Coach {
  firstName: string;
  lastName: string;
  avatarPath?: string | null;
  avatarVersion?: number;
  avatarUrl?: string | null;
  timezone?: string | null;
  weekStartsOn?: WeekStartsOn;
}

export interface Org {
  id: string;
  name: string;
  slug?: string | null;
  createdAt?: string | null;
  setupComplete: boolean;
  weightUnitDefault: "kg" | "lbs";
  distanceUnitDefault: "km" | "mi";
  primaryColor?: string | null;
  logoPath?: string | null;
  logoVersion?: number | null;
  logoUrl?: string | null;
}

export type SessionOrgOption = {
  id: string;
  name: string;
  logoUrl?: string;
};

export type PermissionsMap = Record<string, boolean>;

export type SessionInitOptions = {
  force?: boolean;
  orgId?: string | null;
};

export interface SessionStore {
  session: {
    userId?: string | null;
    roleKey?: string | null;
    memberType?: string | null;
    hasTeamMembers?: boolean;
    coach?: Coach;
    org?: Org;
    orgs?: SessionOrgOption[];
    activeOrgId?: string | null;
    isSwitchingOrg: boolean;
    permissions?: PermissionsMap;
    subscription?: SessionSubscription;
    entitlements?: SessionEntitlements | null;
    sidebarOpen: boolean;
    dismissedTrustpilotReviewUserIds: string[];
    initialised: boolean;
    needsRefreshAfterRehydrate: boolean;
    initWithUser: (
      user: User,
      onInitialised?: () => void,
      options?: SessionInitOptions
    ) => void;
    initWithUserAsync: (
      user: User,
      options?: SessionInitOptions
    ) => Promise<void>;
    reset: () => void;
    setCoach: (coach: Coach) => void;
    setOrg: (org: Org) => void;
    setOrgs: (orgs: SessionOrgOption[]) => void;
    setActiveOrgId: (orgId: string | null) => void;
    setSwitchingOrg: (isSwitching: boolean) => void;
    setPermissions: (permissions: PermissionsMap | undefined) => void;
    setSubscription: (subscription: SessionSubscription | undefined) => void;
    setSidebarOpen: (open: boolean) => void;
    dismissTrustpilotReview: (userId: string) => void;
  };
}

export const createSessionStore: CombinedStateCreator<SessionStore> = (
  set,
  get
) => ({
  session: {
    initialised: false,
    needsRefreshAfterRehydrate: false,
    activeOrgId: null,
    isSwitchingOrg: false,
    sidebarOpen: true,
    dismissedTrustpilotReviewUserIds: [],

    initWithUserAsync: async (user: User, options?: SessionInitOptions) => {
      return new Promise<void>((resolve) => {
        get().session.initWithUser(user, resolve, options);
      });
    },

    initWithUser: async (
      user: User,
      onInitialised?: () => void,
      options?: SessionInitOptions
    ) => {
      const force = options?.force ?? false;
      const currentOrgId =
        get().session.org?.id ?? get().session.activeOrgId ?? null;
      const requestedOrgId = options?.orgId ?? get().session.activeOrgId ?? null;
      const orgChanged =
        requestedOrgId != null && requestedOrgId !== currentOrgId;
      if (options?.orgId) {
        set((state) => {
          state.session.activeOrgId = options.orgId ?? null;
        });
      }
      if (get().session.initialised && !force && !orgChanged) {
        console.warn("[initWithUser] Session is already initialised");
        onInitialised?.();
        return;
      }
      console.log("[initWithUser] initialising session");

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        console.warn(
          "[initWithUser] Session missing while fetching organisation and coach",
        );
        get().session.reset();
        onInitialised?.();
        return;
      }

      const previousUserId = get().session.userId ?? null;
      const previousOrgId = get().session.org?.id ?? null;
      const previousSubscription = get().session.subscription;
      const { trpcVanilla } = await import("@/lib/trpc/vanilla");
      const { applyAuthBootstrapCaches } = await import(
        "@/lib/auth/sessionBootstrap"
      );
      let bootstrap: Awaited<
        ReturnType<(typeof trpcVanilla)["auth"]["getSession"]["query"]>
      >;
      try {
        bootstrap = await trpcVanilla.auth.getSession.query({
          orgId: requestedOrgId,
        });
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Unable to load your organisation";
        console.error("[initWithUser] Error fetching organisation and coach", error);
        toast.error(message);
        await supabase.auth.signOut();
        get().session.reset();
        onInitialised?.();
        return;
      }

      const orgs: SessionOrgOption[] = bootstrap.memberships.map((membership) => ({
        id: membership.org.id,
        name: membership.org.name,
        logoUrl: membership.org.logo?.url,
      }));

      if (bootstrap.memberships.length === 0 || !bootstrap.selected) {
        console.warn(
          "[initWithUser] No organisation memberships found for user",
          user.id,
        );
        set((state) => {
          state.session.initialised = true;
          state.session.needsRefreshAfterRehydrate = false;
          state.session.userId = user.id;
          state.session.roleKey = null;
          state.session.memberType = null;
          state.session.hasTeamMembers = false;
          state.session.coach = bootstrap.coach
            ? {
                firstName: bootstrap.coach.firstName,
                lastName: bootstrap.coach.lastName,
                avatarPath: bootstrap.coach.avatarPath,
                avatarVersion: bootstrap.coach.avatarVersion,
                avatarUrl: bootstrap.coach.avatar?.url ?? null,
                timezone: bootstrap.coach.timezone,
                weekStartsOn: readSessionWeekStartsOn(bootstrap.coach),
              }
            : undefined;
          state.session.org = undefined;
          state.session.orgs = orgs;
          state.session.activeOrgId = null;
          state.session.permissions = undefined;
          state.session.subscription = undefined;
          state.session.entitlements = null;
        });
        onInitialised?.();
        return;
      }

      const selectedOrg = bootstrap.memberships.find(
        (membership) => membership.orgId === bootstrap.selected?.orgId,
      )?.org;

      const coach: Coach = bootstrap.coach
        ? {
            firstName: bootstrap.coach.firstName,
            lastName: bootstrap.coach.lastName,
            avatarPath: bootstrap.coach.avatarPath,
            avatarVersion: bootstrap.coach.avatarVersion,
            avatarUrl: bootstrap.coach.avatar?.url ?? null,
            timezone: bootstrap.coach.timezone,
            weekStartsOn: readSessionWeekStartsOn(bootstrap.coach),
          }
        : {
            firstName: "Coach",
            lastName: "Coach",
            avatarPath: null,
            avatarVersion: undefined,
            avatarUrl: null,
            timezone: null,
            weekStartsOn: DEFAULT_WEEK_STARTS_ON,
          };

      const org: Org | undefined = selectedOrg
        ? {
            id: selectedOrg.id,
            name: selectedOrg.name,
            slug: selectedOrg.slug,
            createdAt: selectedOrg.createdAt,
            setupComplete: selectedOrg.setupComplete,
            weightUnitDefault:
              selectedOrg.weightUnitDefault === "lbs" ? "lbs" : "kg",
            distanceUnitDefault:
              selectedOrg.distanceUnitDefault === "mi" ? "mi" : "km",
            primaryColor: selectedOrg.primaryColor,
            logoPath: selectedOrg.logoPath,
            logoVersion: selectedOrg.logoVersion,
            logoUrl: selectedOrg.logo?.url ?? null,
          }
          : undefined;

      if (
        (previousUserId && previousUserId !== user.id) ||
        (previousOrgId &&
          bootstrap.selected?.orgId &&
          previousOrgId !== bootstrap.selected.orgId)
      ) {
        clearUserCaches();
      }

      let subscriptionRows = bootstrap.subscriptions as
        | SessionSubscription[]
        | null
        | undefined;
      if (!selectActiveSubscription(subscriptionRows ?? [])) {
        try {
          subscriptionRows = await trpcVanilla.billing.subscription.query();
        } catch (error) {
          console.error("[initWithUser] Error fetching subscription", error);
          subscriptionRows = subscriptionRows ?? null;
        }
      }

      const { subscription, entitlements } = applyAuthBootstrapCaches({
        subscriptions: subscriptionRows,
        entitlements: bootstrap.entitlements as SessionEntitlements | null,
        selected: bootstrap.selected,
      });

      set((state) => {
        state.session.initialised = true;
        state.session.needsRefreshAfterRehydrate = false;
        state.session.userId = user.id;
        state.session.roleKey = bootstrap.selected?.roleKey ?? null;
        state.session.memberType = bootstrap.selected?.memberType ?? null;
        state.session.hasTeamMembers =
          bootstrap.selected?.hasTeamMembers ?? false;
        state.session.coach = coach;
        state.session.org = org;
        state.session.orgs = orgs;
        state.session.activeOrgId = bootstrap.selected?.orgId ?? null;
        state.session.permissions = bootstrap.selected
          ?.permissions as PermissionsMap | undefined;
        state.session.subscription = subscription ?? previousSubscription;
        state.session.entitlements = entitlements;
      });

      onInitialised?.();
    },

    setCoach: (coach) => {
      set((state) => {
        state.session.coach = coach;
      });
    },

    setOrg: (org) => {
      set((state) => {
        const previousCreatedAt = state.session.org?.createdAt;
        state.session.org = {
          ...org,
          createdAt: org.createdAt ?? previousCreatedAt ?? null,
        };
        if (state.session.orgs) {
          state.session.orgs = state.session.orgs.map((item) =>
            item.id === org.id ? { ...item, name: org.name } : item,
          );
        }
      });
    },

    setOrgs: (orgs) => {
      set((state) => {
        state.session.orgs = orgs;
      });
    },

    setActiveOrgId: (orgId) => {
      set((state) => {
        state.session.activeOrgId = orgId;
      });
    },

    setSwitchingOrg: (isSwitching) => {
      set((state) => {
        state.session.isSwitchingOrg = isSwitching;
      });
    },

    setPermissions: (permissions) => {
      set((state) => {
        state.session.permissions = permissions;
      });
    },

    setSubscription: (subscription) => {
      set((state) => {
        state.session.subscription = subscription;
      });
    },

    setSidebarOpen: (open) => {
      set((state) => {
        state.session.sidebarOpen = open;
      });
    },

    dismissTrustpilotReview: (userId) => {
      set((state) => {
        const ids = state.session.dismissedTrustpilotReviewUserIds ?? [];
        if (!state.session.dismissedTrustpilotReviewUserIds) {
          state.session.dismissedTrustpilotReviewUserIds = ids;
        }
        if (!ids.includes(userId)) ids.push(userId);
      });
    },

    reset: () => {
      set((state) => {
        state.session.initialised = false;
        state.session.needsRefreshAfterRehydrate = false;
        state.session.userId = null;
        state.session.roleKey = null;
        state.session.memberType = null;
        state.session.hasTeamMembers = false;
        state.session.coach = undefined;
        state.session.org = undefined;
        state.session.orgs = undefined;
        state.session.activeOrgId = null;
        state.session.isSwitchingOrg = false;
        state.session.permissions = undefined;
        state.session.subscription = undefined;
        state.session.entitlements = null;
        state.session.sidebarOpen = true;
        state.chatDrafts = {};
        state.upgradeDialog.isOpen = false;
      });
      clearUserCaches();
      void import("@/features/onboarding/signupQuestionnaire").then((mod) => {
        mod.clearSignupQuestionnaireCompleted();
      });
    },
  },
});
